// Paso 08 — enganche del crédito (+ lo de contado): `POST …/down-payment`, que genera el folio ENG.
// Cobro en renglones fijos; al confirmar sigue a la firma.
import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';
import { ArrowRight } from 'lucide-react';
import { Button, Loading } from '@/kit';
import { formatMXN } from '@/shared/lib/format/money';
import { ApiError } from '@/api/errors';
import { useContractPreview, useDownPayment, useWizardSession } from '../api/useWizard';
import { useCatalogLineInfo } from '../api/useCatalogLineInfo';
import { useChargeableLines } from '../api/useChargeableLines';
import { wizardErrorMsg } from '../api/wizardError';
import { chargeFailure } from '../chargeErrors';
import { chargeLinesOf, downPaymentChargeCents } from '../saleCharge';
import { UnchargeableLinesNotice } from '../components/UnchargeableLinesNotice';
import { ChargeReloadNotice } from '../components/ChargeReloadNotice';
import { PromotionRevalidationNotice } from '../components/PromotionRevalidationNotice';
import { pendingRevalidationLineIds } from '../promotionRevalidation';
import { WizardCard, WizardCardBody, WizardCardHead } from '../components/WizardCard';
import { WizardBackLink, WizardFootNav } from '../components/WizardFootNav';
import { FixedPaymentRowsForm } from '../components/FixedPaymentRowsForm';
import { NO_CHARGE_LABEL } from '../payments';
import { toSalePayments } from '../salePayments';
import {
    initialSplits,
    rowsBlocker,
    rowsFromRegistered,
    rowsShapeBlocker,
    rowsToDrafts,
    syncRowsTotal,
    type FixedPaymentRows,
} from '../fixedPaymentRows';
import { catalogDisplayName } from '@/shared/lib/format/itemDisplayName';

const HINT = 'text-[11px] leading-[1.35] text-muted-foreground';

export function DownPaymentStep({ sessionId }: { sessionId: string }) {
    const navigate = useNavigate();
    const sessionQuery = useWizardSession(sessionId);
    const lineInfo = useCatalogLineInfo(sessionId);
    const session = sessionQuery.data;
    const downPayment = useDownPayment(sessionId);
    const chargeable = useChargeableLines(sessionId, session);

    const [rows, setRows] = useState<FixedPaymentRows>(() => initialSplits(0));
    /** Total con el que se armaron los renglones; si cambia, el efectivo toma la diferencia. */
    const [rowsTotal, setRowsTotal] = useState(0);
    /** Rechazo porque la venta cambió: queda en pantalla y frena el cobro hasta recargar. */
    const [reloadMessage, setReloadMessage] = useState<string>();

    const paid = !!session?.payment;
    const creditId = session?.loan?.creditId;
    // El monto del enganche vive en loan-credit (la sesión solo guarda punteros): se rehidrata con
    // contract/preview. Ya cobrado no hace falta — la sesión trae payment.amountCents y el folio.
    const preview = useContractPreview(sessionId, !!session && !paid && !!creditId);
    const credit = preview.data?.credit;

    const pendingTotal =
        session && credit && !paid ? downPaymentChargeCents(session, credit.downPaymentCents) : 0;
    if (pendingTotal !== rowsTotal) {
        setRowsTotal(pendingTotal);
        setRows((prev) => syncRowsTotal(prev, pendingTotal));
    }

    if (!session) return <Loading size="lg" />;
    const product = session.product;

    // Sin crédito originado no hay monto que cobrar — la cotización es el paso anterior.
    if (!creditId) {
        return (
            <WizardCard>
                <WizardCardHead eyebrow="Crédito SureKeep" title="Registrar el enganche" />
                <WizardCardBody>
                    <p className="text-[12.5px] text-muted-foreground">
                        Todavía no hay un crédito cotizado. Regresa a Cotización y confírmala.
                    </p>
                </WizardCardBody>
                <WizardFootNav
                    back={
                        <WizardBackLink
                            sessionId={sessionId}
                            step="quote"
                            label="← Volver a la cotización"
                        />
                    }
                />
            </WizardCard>
        );
    }

    const hasLines = session.reservedLines !== undefined;
    const revalidationPending = pendingRevalidationLineIds(session).length > 0;
    const lines = chargeLinesOf(session, { credit: true, ...lineInfo });
    // Enganche del crédito + lo que va de contado, con la misma regla que valida el silo.
    const chargeCents = credit ? downPaymentChargeCents(session, credit.downPaymentCents) : 0;
    const amountCents = paid ? (session.payment?.amountCents ?? 0) : chargeCents;
    const amount = formatMXN(amountCents / 100);
    // Ya cobrado el preview no corre: el reparto congelado de las líneas dice si hubo contado.
    const withCashLines = paid
        ? hasLines && lines.some((l) => (l.cashCents ?? 0) > 0)
        : credit !== undefined && chargeCents > credit.downPaymentCents;

    const valid = rowsBlocker(rows, chargeCents) === null;
    const balanceLabel = rowsShapeBlocker(rows, chargeCents);

    const confirmar = async (): Promise<void> => {
        if (!credit) return;
        try {
            const r = await downPayment.mutateAsync({
                amountCents: chargeCents,
                payments: toSalePayments(rowsToDrafts(rows), chargeCents),
            });
            toast.success(
                `${withCashLines ? 'Cobrado hoy' : 'Enganche cobrado'} — folio ${r.folio}`,
            );
            void navigate({
                to: '/wizard/s/$sessionId/$step',
                params: { sessionId, step: 'sign' },
            });
        } catch (e) {
            const failure = chargeFailure(e);
            if (failure) {
                toast.error(failure.message);
                if (failure.recovery === 'product') {
                    void navigate({
                        to: '/wizard/s/$sessionId/$step',
                        params: { sessionId, step: 'product' },
                    });
                } else if (failure.recovery === 'build-sale') {
                    void sessionQuery.refresh();
                    void navigate({
                        to: '/wizard/s/$sessionId/$step',
                        params: { sessionId, step: 'build-sale' },
                    });
                } else if (failure.recovery === 'quote') {
                    void navigate({
                        to: '/wizard/s/$sessionId/$step',
                        params: { sessionId, step: 'quote' },
                    });
                } else if (failure.recovery === 'reload') {
                    setReloadMessage(failure.message);
                }
                return;
            }
            if (e instanceof ApiError) {
                if (e.code === 'PAYMENT_ALREADY_REGISTERED') {
                    toast.error('Esta venta ya tenía un cobro registrado — se recargó el estado.');
                    void sessionQuery.refresh();
                    return;
                }
            }
            // Cualquier otro error tipado del silo trae msg en español — se muestra ese.
            toast.error(wizardErrorMsg(e, 'No se pudo registrar el cobro — intenta de nuevo.'));
        }
    };

    return (
        <WizardCard>
            <WizardCardHead
                eyebrow="Crédito SureKeep"
                title="Registrar el enganche"
                sub={
                    product ? (
                        <>
                            Equipo:{' '}
                            <strong className="font-semibold text-foreground">
                                {catalogDisplayName(product, lineInfo.descriptions)}
                            </strong>
                            {credit ? (
                                <>
                                    {' '}
                                    · Cuota semanal:{' '}
                                    <span className="tabular-nums">
                                        {formatMXN(credit.weeklyPaymentWithIvaCents / 100)}
                                    </span>{' '}
                                    × {credit.termWeeks} semanas
                                </>
                            ) : null}
                        </>
                    ) : undefined
                }
            />

            <WizardCardBody className="flex flex-col gap-5">
                {!paid && preview.isPending ? (
                    <Loading size="lg" />
                ) : !paid && preview.isError ? (
                    <div className="flex flex-col items-center gap-3 py-8 text-center">
                        <p className="text-[12.5px] text-muted-foreground">
                            {wizardErrorMsg(
                                preview.error,
                                'No se pudo leer el monto del enganche del motor de crédito.',
                            )}
                        </p>
                        <Button
                            size="sm"
                            isLoading={preview.isFetching}
                            onClick={() => void preview.refetch()}
                        >
                            Reintentar
                        </Button>
                    </div>
                ) : (
                    <>
                        {/* .amount-hero — monto del enganche + folio ENG de conciliación. */}
                        <div className="rounded-xl bg-[linear-gradient(135deg,hsl(var(--primary)),hsl(222_47%_20%))] px-[22px] py-5 text-primary-foreground">
                            <div className="text-[10px] font-semibold uppercase tracking-[0.08em] opacity-[0.82]">
                                {paid
                                    ? withCashLines
                                        ? 'Cobrado hoy'
                                        : 'Enganche cobrado'
                                    : 'Monto a cobrar'}
                            </div>
                            <div className="mt-1 text-[34px] font-bold leading-[1.05] tracking-[-0.02em] tabular-nums">
                                {amount}
                            </div>
                            <div className="mt-1 text-[12.5px] opacity-[0.88]">
                                {hasLines
                                    ? withCashLines
                                        ? 'Enganche + contado'
                                        : 'Enganche'
                                    : `Enganche pactado${product ? ` para ${catalogDisplayName(product, lineInfo.descriptions)}` : ''}`}
                            </div>
                            <div className="mt-2 border-t border-primary-foreground/20 pt-2">
                                <div className="text-[9.5px] uppercase tracking-wider opacity-70">
                                    Folio de cobro
                                </div>
                                <div className="font-mono text-[13px] font-semibold tracking-[0.02em]">
                                    {session.folio ?? 'se genera al confirmar el cobro'}
                                </div>
                            </div>
                        </div>

                        {!paid && revalidationPending && (
                            <PromotionRevalidationNotice sessionId={sessionId} />
                        )}

                        {!paid && (
                            <ChargeReloadNotice
                                message={reloadMessage}
                                onReload={async () => {
                                    // El crédito o las líneas cambiaron en otra pantalla: se releen los dos.
                                    await Promise.all([preview.refetch(), sessionQuery.refresh()]);
                                    setReloadMessage(undefined);
                                }}
                            />
                        )}

                        {!paid && (
                            <UnchargeableLinesNotice
                                lines={chargeable}
                                onReload={() => void sessionQuery.refresh()}
                                onFixLost={() => {
                                    void navigate({
                                        to: '/wizard/s/$sessionId/$step',
                                        params: { sessionId, step: 'build-sale' },
                                    });
                                }}
                            />
                        )}

                        {paid ? (
                            /* El dinero ya entró: los renglones solo se leen y nunca se vuelve a cobrar. */
                            <>
                                {session.payment && amountCents > 0 ? (
                                    <FixedPaymentRowsForm
                                        readOnly
                                        totalCents={amountCents}
                                        rows={rowsFromRegistered(session.payment)}
                                    />
                                ) : (
                                    <p className="text-[13px] font-semibold">{NO_CHARGE_LABEL}</p>
                                )}
                                <p className={HINT}>El crédito se activa al terminar la entrega.</p>
                            </>
                        ) : chargeCents === 0 ? (
                            <p className={HINT}>No hay nada que cobrar: confirma para continuar.</p>
                        ) : (
                            <FixedPaymentRowsForm
                                rows={rows}
                                onChange={setRows}
                                totalCents={chargeCents}
                            />
                        )}
                    </>
                )}
            </WizardCardBody>

            <WizardFootNav back={<WizardBackLink sessionId={sessionId} step="quote" />}>
                {paid ? (
                    <Button
                        rightIcon={<ArrowRight className="size-3.5" aria-hidden />}
                        onClick={() =>
                            void navigate({
                                to: '/wizard/s/$sessionId/$step',
                                params: { sessionId, step: 'sign' },
                            })
                        }
                    >
                        Continuar a la firma
                    </Button>
                ) : (
                    <Button
                        disabled={
                            !valid ||
                            !credit ||
                            chargeable.blocked ||
                            !!reloadMessage ||
                            revalidationPending
                        }
                        isLoading={downPayment.isPending}
                        rightIcon={<ArrowRight className="size-3.5" aria-hidden />}
                        onClick={() => void confirmar()}
                    >
                        {balanceLabel ?? 'Confirmar cobro y continuar'}
                    </Button>
                )}
            </WizardFootNav>
        </WizardCard>
    );
}
